import React from "react";
import { useTranslation } from "react-i18next";
import LegalPageShell from "../../components/LegalPageShell";
import LegalSection from "../../components/legal/LegalSection";

export default function PrivacyPolicy() {
  const { t } = useTranslation(["legalPrivacy", "common", "legal"]);

  const sections = [
    { id: "introduction", label: t("introduction", "Introduction") },
    { id: "information-collected", label: t("informationCollected", "Information Collected") },
    { id: "how-information-used", label: t("howInformationIsUsed", "How Information Is Used") },
    { id: "data-sharing-processors", label: t("dataSharingAndProcessors", "Data Sharing and Processors") },
    { id: "your-rights-controls", label: t("yourRightsAndControls", "Your Rights and Controls") },
    { id: "retention-deletion", label: t("retentionAndDeletion", "Retention and Deletion") },
  ];

  return (
<LegalPageShell
        title={t("title", "Privacy Policy")}
        subtitle={t("subtitle", "How Orrith collects, uses, stores, and protects information across the Cycle of Certainty platform.")}
        effectiveDate={`Effective: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`}
        sections={sections}
        namespace="legalPrivacy"
      >
      <LegalSection
        id="introduction"
        title={t("introduction", "Introduction")}
      >
        <p className="legal-prose">
          {t("introDesc", "Orrith (\"we,\" \"our,\" \"us\") operates the Orrith platform at orrith.me and related web experiences including NorthStar, Farm, FarmPulse, FarmMark, messaging, profile, support, and settings features. This Privacy Policy explains what information we collect, how we use it, who we share it with, and your rights regarding your data. By using our platform, you agree to the collection and use of information in accordance with this policy.")}
        </p>
        <p className="legal-prose mt-4">
          {t("introScope", "This policy applies to all Orrith web experiences and describes data that is actually processed by the current production application. We may update this policy when product behavior, infrastructure, or legal requirements change. The effective date and version shown at the top of this page indicate the currently active policy text.")}
        </p>
      </LegalSection>

      <section id="information-collected" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("informationCollected", "Information Collected")}
        </h2>
        <p className="legal-prose mb-6">
          {t("infoCollectedIntro", "We collect information you provide directly to us, information we gather automatically when you use our platform, and information we receive from third parties.")}
        </p>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("accountAndAuthentication", "Account and Authentication")}
        </h3>
        <ul className="legal-list">
          <li>{t("accountAuth.emailPassword", "Email and password for account creation and sign-in")}</li>
          <li>{t("accountAuth.googleSignIn", "Google sign-in account data when you use Google authentication")}</li>
          <li>{t("accountAuth.phoneOTP", "Optional phone number and OTP verification data when phone verification is used")}</li>
          <li>{t("accountAuth.emailVerification", "Email verification status and authentication metadata from Firebase Authentication")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("profileAndSettings", "Profile and Settings")}
        </h3>
        <ul className="legal-list">
          <li>{t("profileSettings.usernameDisplay", "Username, display name, profile photo, bio, and profile visibility choices")}</li>
          <li>{t("profileSettings.locationFields", "Location fields you provide (province, district, sector, cell)")}</li>
          <li>{t("profileSettings.privacyToggles", "Privacy toggles for profile fields and profile visibility")}</li>
          <li>{t("profileSettings.preferences", "Theme, language, notification, and communication preferences")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("userContentAndActivity", "User Content and Activity")}
        </h3>
        <ul className="legal-list">
          <li>{t("userContent.listingsOffers", "Product listings, offers, wishlist items, ratings, and reviews")}</li>
          <li>{t("userContent.messages", "Messages and chat metadata for participant conversations")}</li>
          <li>{t("userContent.farmPulse", "FarmPulse contributions (price submissions and planting intention records)")}</li>
          <li>{t("userContent.supportTickets", "Support tickets, feedback submissions, and optional error reports")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("technicalAndDeviceData", "Technical and Device Data")}
        </h3>
        <ul className="legal-list">
          <li>{t("technicalData.browserLanguage", "Browser language and interface preferences")}</li>
          <li>{t("technicalData.errorContext", "Runtime error context when you submit an error report (for example: page URL, user agent, stack snippet)")}</li>
          <li>{t("technicalData.clientCache", "Client-side cache and draft data stored in browser storage")}</li>
          <li>{t("technicalData.networkTimestamp", "Network and timestamp data required to process requests")}</li>
        </ul>
      </section>

      <section id="how-information-used" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("howInformationIsUsed", "How Information Is Used")}
        </h2>
        <p className="legal-prose mb-6">
          {t("howUsedIntro", "We use the information we collect to provide, maintain, and improve our platform. Specifically, we use your information for the following purposes:")}
        </p>
        <ul className="legal-list">
          <li>{t("howUsed.authenticate", "To authenticate users and secure access to protected features")}</li>
          <li>{t("howUsed.profilesListings", "To create and display profiles, listings, offers, and impact activity")}</li>
          <li>{t("howUsed.messagingSupport", "To operate messaging, claims, notifications, and support workflows")}</li>
          <li>{t("howUsed.moderation", "To moderate content, investigate abuse reports, and enforce platform rules")}</li>
          <li>{t("howUsed.reliability", "To maintain product reliability, diagnose failures, and improve user experience")}</li>
        </ul>
      </section>

      <section id="data-sharing-processors" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("dataSharingAndProcessors", "Data Sharing and Processors")}
        </h2>
        <p className="legal-prose mb-6">
          {t("dataSharing.noSell", "Orrith does not sell personal data. Data is processed through service providers needed to operate the platform. We share information with the following categories of processors:")}
        </p>
        <ul className="legal-list">
          <li>{t("dataSharing.firebase", "Firebase services for authentication, data storage, and application operations")}</li>
          <li>{t("dataSharing.imageUpload", "Configured image upload service when users upload listing photos")}</li>
          <li>{t("dataSharing.translation", "Configured translation endpoint when auto-translation is enabled by deployment settings")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("storageHostingAndProcessors", "Storage, Hosting, and Processors")}
        </h3>
        <p className="legal-prose mb-4">
          {t("storage.firebaseServices", "Orrith uses Firebase services (including Authentication, Firestore, Storage, and Functions client integrations) for core account and application data processing.")}
        </p>
        <p className="legal-prose mb-4">
          {t("storage.imageUpload", "If you upload listing images, media may be processed through the configured image upload service used by the application.")}
        </p>
        <p className="legal-prose">
          {t("storage.translation", "If automatic translation is enabled and used, message text may be sent to the configured translation provider endpoint to return translated output.")}
        </p>
      </section>

      <section id="your-rights-controls" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("yourRightsAndControls", "Your Rights and Controls")}
        </h2>
        <p className="legal-prose mb-6">
          {t("rightsIntro", "You have control over your data and privacy on Orrith:")}
        </p>
        <ul className="legal-list">
          <li>{t("dataControl.profileInfo", "You choose what profile information to provide and what profile fields are visible")}</li>
          <li>{t("dataControl.locationSharing", "You control whether location sharing is enabled for pickup coordination")}</li>
          <li>{t("dataControl.exportData", "You can export supported account data as JSON in Settings")}</li>
          <li>{t("dataControl.accountDeletion", "You can request account deletion in Settings (subject to security checks like recent login requirements)")}</li>
          <li>{t("dataControl.fieldVisibility", "You can manage individual field visibility for email, phone, avatar, location, and bio")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("whatOrrithDoesNotCollect", "What Orrith Does Not Collect By Design")}
        </h3>
        <ul className="legal-list">
          <li>{t("notCollect.cardNumbers", "No in-app card numbers or bank account credentials are collected by current checkout flows")}</li>
          <li>{t("notCollect.biometric", "No biometric identifiers are requested in the current web application")}</li>
          <li>{t("notCollect.govId", "No government ID upload workflow is implemented in the current platform")}</li>
          <li>{t("notCollect.gpsTracking", "No always-on background GPS tracking is implemented")}</li>
        </ul>

        <h3 className="text-display-sm font-medium text-gray-900 dark:text-white mt-8 mb-3">
          {t("securityAndAccessControls", "Security and Access Controls")}
        </h3>
        <p className="legal-prose">
          {t("securityControls.accessControl", "Access to Firestore data is controlled through Firebase security rules and authenticated session context. Role-based restrictions are enforced for administrative operations. Data transmission to platform services is performed over HTTPS.")}
        </p>
      </section>

      <section id="retention-deletion" tabIndex={-1}>
        <h2 className="text-display-md font-semibold text-gray-900 dark:text-white mb-4">
          {t("retentionAndDeletion", "Retention and Deletion")}
        </h2>
        <ul className="legal-list">
          <li>{t("retention.accountContent", "Account and content records remain until deleted by user action, moderation, or operational cleanup")}</li>
          <li>{t("retention.browserData", "Browser-side draft and preference data remains until you clear it or the app overwrites it")}</li>
          <li>{t("retention.supportRecords", "Support and feedback records may be retained for service continuity and abuse prevention")}</li>
          <li>{t("retention.accountDeletion", "Account deletion attempts remove supported user-linked records and authentication account where permitted")}</li>
        </ul>

        <p className="legal-prose mt-6">
          {t("policyUpdates.desc", "We may update this policy when product behavior, infrastructure, or legal requirements change. The effective date and version shown at the top of this page indicate the currently active policy text. For questions about this policy, contact us at legal@orrith.me.")}
        </p>
      </section>
    </LegalPageShell>
  );
}